import { CalcMethod, LotState, type LotType, ProjectState } from '$types/enums'
import type { AddressData } from '$types/addressData'
import type { AttachmentView } from '$types/attachment'
import type { MiniLotMatchRequest } from '$mapapp/types/miniLot'

export type slideInModes =
	'project' | 'lot' | 'pop' | 'request' | 'matching' | 'minilot' | 'returnFreight' | 'cluster'

export type clusterListViewModes =
	'project' | 'lot' | 'pop' | 'profile' | 'request' | 'returnFreight'

export type slideInViewModes = 'cluster' | 'detail'

export enum PopMaintenance {
	MAINTAINED = 'MAINTAINED',
	NOT_MAINTAINED = 'NOT_MAINTAINED',
	NEVER_MAINTAINED = 'NEVER_MAINTAINED'
}

type PopHeaderProps = {
	label: string
	ownSite: boolean
	types: LocationType[]
	address: string
	maintained: PopMaintenance
	maintainedDate: string
}

export type Pop = {
	id: string
	contact: Contact
	attachments: AttachmentView[]
	comments: Comment[]
	materialProvides: string[]
	materialAccepts: string[]
	tenantId: string
	popType: PopType
} & PopHeaderProps

type PopType = 'POP' | 'PARTNER'

export type LocationType = {
	code: string
	colorHex: string
	id: string
	shortText: string
	priority: number
}

export type ProfileType = 'ACCEPT_MATERIAL' | 'PROVIDE_MATERIAL'

export type PopProfile = {
	id: string
	label: string
	materialLabel: string
	profileType: ProfileType
	materialProperties: MaterialPropertyInfoDto[] // FIXME: Check what we need from the BE, but this depends on the design (TBD...)
	attachments: AttachmentView[]
	comments: Comment[]
	pricePerTon: number
	popId: string // Reference to parent Pop - used for matching
}

export type ReturnFreightData = {
	id: string
}

export type ReturnFreightInfo = {
	returnFreightLotIds: string[]
}

export type ReturnFreightRequest = {
	referenceLotId: string
	destinationPopId: string
	minDistance: number
	maxDistance: number
}

export type Contact = {
	name?: string
	phone?: string
	mobile?: string
	email?: string
	link?: string
}

export type Request = Omit<
	Lot,
	'materialPropertiesComment' | 'materialPropertiesCommentWithoutOptional'
> & { offerUrl: string }

export enum OptimizerMailsStatus {
	NONE = 'NONE',
	MAIL_SCHEDULED = 'MAIL_SCHEDULED',
	SENT = 'SENT'
}

export type LotHeaderProps = {
	volume: number | null
	materialName: string
	recyclingClasses: string[] // Verwertungsklassen, potenziell mehrere
	timeframe: string
	type: LotType
	lotState?: LotState
	address: string
	requesterSiteLocation: AddressData
	offerSiteLocation: AddressData | null
	pricePerTon: number
	optimizerMailsStatus: OptimizerMailsStatus
}

export type Lot = {
	id: string
	correlationId: string
	label: string
	code: string
	calcMethod: CalcMethod
	contact: Contact
	materialProperties: MaterialPropertyInfoDto[]
	attachments: AttachmentView[]
	comments: Comment[]
	materialPropertiesComment?: string
	materialPropertiesCommentWithoutOptional?: string
	offerSiteLat?: number
	offerSiteLon?: number
} & LotHeaderProps

export type Project = {
	id: string
	label: string
	state: ProjectState
	code: string
	timeframe: string
	address: string
	contact: Contact
	attachments: AttachmentView[]
	comments: Comment[]
	lots: string[]
}

export type MatchingOverview = {
	matchOverviews: MatchOverviewData[]
	requestId: string
	endMessage: boolean
}

export type MatchOverviewData = {
	partner: LotMatchPartner | PopMatchPartner
	circulationInformation: CirculationInformation
	priceInformation: MatchPriceInformation

	// some more data in case of conditional match or substitute match
	conditionalMatch: boolean
	substituteMatch: boolean
	underlyingAnalytical: MatchAnalytical
	recommendation: string
	catchmentAreaRequirement: boolean | undefined
	platformRequestPossible: boolean
}

export type MatchAnalytical = {
	id: string
	name: string
	technicalName: string
}

export type LotMatchPartner = { calcMethod: CalcMethod } & MatchOverviewPartner & LotHeaderProps

export type PopMatchPartner = {
	matchingProfileId: string
	matchingProfileName: string
} & MatchOverviewPartner &
	PopHeaderProps

type MatchOverviewPartner = {
	label: string
	objType: 'lot' | 'pop'
	objId: string // No type prefix, just id
	providesMaterial: boolean
}

export type CirculationInformation = {
	oneWayTime: number // Minutes
	oneWayDistance: number
	circularTime: number // Minutes
	circularDistance: number
	co2Emission: number
}

export type MatchCoordinates = {
	requestId: string
	lat: number
	lon: number
	endMessage?: boolean
}

export type MiniLotMaterialProperties = {
	requestId: string
	recyclingClasses: string[]
	materialProperties: MaterialPropertyInfoDto[]
	endMessage?: boolean
}
/**
 * Request to the TransportCostService. Can be used for requesting calculations for random routes.
 * startId, endId can be existing entities from our system OR null in case of random place (result from route planner search)
 * distance ONE-WAY distance in kilometers between start and end location
 * duration ROUND_TRIP duration in seconds between start and end location.
 */
export type TransportCostRequest = {
	startId: string | null
	startType: string | null
	endId: string | null
	endType: string | null
	distance: number
	duration: number
}

/**
 * Response from the TransportCostService. Will contain either of the data.
 */
export type TransportCostResponse = {
	vehicleTransportCosts: TransportCostWithDailyCost[] | null
	flatrateTransportCosts: number | null
}

/**
 * Includes detailed information about costs per vehicle and daily costs of tenant
 */
type TransportCostWithDailyCost = {
	vehicleCategory: string
	dailyRate: number
	dailyPerformance: number
	costPerTonAndDistance: number
}

export type MatchPriceInformation = {
	pricePerTon: number
	transportCost: TransportCostResponse
	partnerPricePerTon: string // can be the number or if we have a request offer and are < OPTIMIZED, it can also be "—"
	volume: number
}

export type Comment = {
	label: string
	text: string
}

export type PlatformRequestMailInfo = {
	isRequested: boolean
	toolTipText: string
}

/**
 * WebSocket payload for /requestMatchMarker. Mirrors backend MapWebsocketController.RequestMatchMarkerMessage.
 */
type RequestMatchMarkerMessage = {
	referenceType: 'lot' | 'pop' | 'miniLot'
	/** Distance range min, in km */
	radiusFrom?: number
	/** Distance range max, in km */
	radiusTo?: number
	requestId?: string
}

export type MiniLotRequestMatchMarkerMessage = {
	miniLot: MiniLotMatchRequest
} & RequestMatchMarkerMessage

export type GenericRequestMatchMarkerMessage = {
	lat?: number
	lon?: number
	referenceId: string
	referenceType: 'lot' | 'pop' | 'miniLot'
	profileId?: string
} & RequestMatchMarkerMessage

/**
 * This is a list of the key-value pairs of one material property and all its sub-properties that are filled
 */
export type MaterialPropertyInfoDto = { key: string; value: string }[]
